import {
  all,
  call,
  delay,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';

import {
  createPayoffStatementFailure,
  createPayoffStatementSuccess,
  fetchPayoffDatesFailure,
  fetchPayoffDatesSuccess,
  fetchPayoffDocumentFailure,
  fetchPayoffDocumentSuccess,
  fetchPayoffHistoryFailure,
  fetchPayoffHistorySuccess,
  fetchPayoffStatement,
  fetchPayoffStatementFailure,
  fetchPayoffStatementSuccess,
} from './actions';
import {
  CREATE_PAYOFF_STATEMENT,
  FETCH_ERROR_MESSAGE,
  FETCH_PAYOFF_DATES,
  FETCH_PAYOFF_DOCUMENT,
  FETCH_PAYOFF_HISTORY,
  FETCH_STATEMENT_ERROR_MESSAGE,
  IS_INELIGIBLE,
  IS_MANCODE_Y,
  PAYOFF_DATES_BANNER_ERROR,
  PAYOFF_REASON_CODE_DICTIONARY,
  POPUP_ERROR_MESSAGE,
} from './constants';
import {
  getStatementToken,
  removeStatementToken,
  setStatementToken,
} from './helpers';
import { updateRefetchStatus as updateDocumentsRefetchStatus } from '../Documents/actions';
import makeSelectMobile from '../Mobile/selectors';
import DesktopGlobalHelpers from '../../utils/DesktopGlobalHelpers';
import MobileDeviceHelpers from '../../utils/MobileDeviceHelpers';
import MobileGlobalHelpers from '../../utils/MobileGlobalHelpers';
import { base64ToArrayBuffer, formatDate } from '../../utils/globalHelpers';
import { get, post, putRequest } from '../../utils/request';

export function* createPayoffStatementSaga({ baseUrl }, { payload }) {
  try {
    const {
      loanNumber,
      payoffDate,
      payoffReason,
      requestType,
    } = payload;

    const {
      error,
      token,
    } = yield call(
      post,
      `${baseUrl}/payoff/create/${loanNumber}`,
      {
        payoffDate,
        payoffReason,
        requestType,
      },
    );
    if (error) {
      yield put(createPayoffStatementFailure());
    } else {
      yield put(fetchPayoffStatement());
      yield call(setStatementToken, token);
      yield put(createPayoffStatementSuccess());
      yield call(fetchPayoffStatementSaga, { baseUrl, loanNumber });
      yield put(updateDocumentsRefetchStatus(true));
    }
  } catch {
    yield put(createPayoffStatementFailure());
  }
}

export function* fetchPayoffStatementSaga({ baseUrl, loanNumber }) {
  try {
    const Token = yield call(getStatementToken);
    const isMobile = yield select(makeSelectMobile('isMobile'));
    while (true) {
      const {
        error,
        payOffDocument,
        requestStatus,
      } = yield call(putRequest, `${baseUrl}/payoff/statement/${loanNumber}`, { Token });
      if (error || requestStatus === 'InvalidRequest' || requestStatus === 'ServiceNotAvailable') {
        const [reasonCode] = error.reasons;
        const displayMessage =
          PAYOFF_REASON_CODE_DICTIONARY[reasonCode] ||
          FETCH_STATEMENT_ERROR_MESSAGE;
        yield put(fetchPayoffStatementFailure(displayMessage));
        break;
      } else if (requestStatus === 'Complete') {
        const { document, documentType } = payOffDocument;
        const status = documentType === 'RejectNotice';
        const fileName = `Payoff_Statement_${loanNumber.slice(-4)}.pdf`;
        let mobileDocument = '';
        if (isMobile) {
          mobileDocument = yield (
            MobileGlobalHelpers.saveToFile({ fileContent: document, fileName })
          );
        }
        yield put(fetchPayoffStatementSuccess({
          document,
          mobileDocument,
          status,
        }));
        break;
      } else {
        yield delay(60000);
      }
    }
  } catch {
    yield put(fetchPayoffStatementFailure(FETCH_STATEMENT_ERROR_MESSAGE));
  } finally {
    yield call(removeStatementToken);
  }
}

export function* payoffDatesSaga({ baseUrl }, { payload }) {
  try {
    const { loanNumber } = payload;
    const {
      availablePayDownDates,
      availablePayOffDates,
      availablePayOffReasons,
      error,
    } = yield call(get, `${baseUrl}/payoff/dates/${loanNumber}`);
    if (error) {
      const [reason] = error.reasons;
      if (reason === '12001.1013') {
        yield put(fetchPayoffDatesFailure(IS_MANCODE_Y));
      } else {
        const partialError = reason === '12001.1003' ? IS_INELIGIBLE : PAYOFF_DATES_BANNER_ERROR;
        yield put(fetchPayoffDatesFailure(partialError));
      }
    } else {
      yield put(fetchPayoffDatesSuccess({
        availablePayDownDates,
        availablePayOffDates,
        availablePayOffReasons,
      }));
    }
  } catch (error) {
    yield put(fetchPayoffDatesFailure());
  }
}

export function* fetchPayoffDocumentSaga({ baseUrl }, { payload }) {
  const {
    dispatchSetFile,
    dispatchToggleDrawer,
    loanNumber,
    token,
  } = payload;
  const isMobile = yield select(makeSelectMobile('isMobile'));
  try {
    const {
      document,
      documentType,
      error,
      payOffDate,
    } = yield call(
      post, `${baseUrl}/payoff/document/${loanNumber}`, { token },
    );
    if (error) {
      yield put(fetchPayoffDocumentFailure(FETCH_ERROR_MESSAGE));
    } else {
      const formattedPayOffDate = formatDate(payOffDate, 'UTC', 'MMM_DD_YYYY');
      const fileName = `${documentType}_${formattedPayOffDate}.pdf`;
      yield put(fetchPayoffDocumentSuccess());

      if (isMobile) {
        yield call(MobileDeviceHelpers.saveAttachmentToFile, {
          dispatchSetFile,
          dispatchToggleDrawer,
          fileContent: document,
          fileName,
        });
      } else {
        const arrayBuffer = yield base64ToArrayBuffer(document);
        const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' });
        yield DesktopGlobalHelpers.openFile(pdfBlob);
      }
    }
  } catch ({ message }) {
    if (message === 'popupBlocked') {
      yield put(fetchPayoffDocumentFailure(POPUP_ERROR_MESSAGE));
    } else {
      yield put(fetchPayoffDocumentFailure(FETCH_ERROR_MESSAGE));
    }
  }
}

export function* payoffHistorySaga({ baseUrl }, { payload }) {
  try {
    const { loanNumber } = payload;
    const { error, items } = yield call(get, `${baseUrl}/payoff/history/${loanNumber}`);
    if (error) {
      yield put(fetchPayoffHistoryFailure());
    } else {
      yield put(fetchPayoffHistorySuccess(items));
    }
  } catch {
    yield put(fetchPayoffHistoryFailure());
  }
}

export default (options) => function* rootSaga() {
  yield all([
    takeLatest(CREATE_PAYOFF_STATEMENT, createPayoffStatementSaga, options),
    takeLatest(FETCH_PAYOFF_DATES, payoffDatesSaga, options),
    takeLatest(FETCH_PAYOFF_DOCUMENT, fetchPayoffDocumentSaga, options),
    takeLatest(FETCH_PAYOFF_HISTORY, payoffHistorySaga, options),
  ]);
};
